import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {AddressDto} from '../types';
import {zipCodeApi} from '../services/api';
import {
  CloseButton as GlobalCloseButton,
  ModalHeader as GlobalModalHeader,
  ModalTitle as GlobalModalTitle,
  ModalBody
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';
import FormInput from './common/FormInput';
import FormContainer from './common/FormContainer';
import FormRow from './common/FormRow';
import AddressAutocomplete from './AddressAutocomplete';

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  flex: 1;

  ${props => props.variant === 'primary' && `
    background-color: ${props.theme.primary || '#e74c3c'};
    color: white;
    box-shadow: 0 4px 6px ${props.theme.primary ? `${props.theme.primary}33` : 'rgba(231, 76, 60, 0.2)'};
    &:hover { 
      background-color: ${props.theme.secondary || '#c0392b'};
      transform: translateY(-1px);
      box-shadow: 0 6px 12px ${props.theme.primary ? `${props.theme.primary}4D` : 'rgba(231, 76, 60, 0.3)'};
    }
    &:disabled { background-color: ${props.theme.primary ? `${props.theme.primary}4D` : '#feb2b2'}; cursor: not-allowed; transform: none; box-shadow: none; }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #f8fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    &:hover { background-color: #edf2f7; }
  `}
`;

interface AddressManageModalProps {
  address?: AddressDto;
  onClose: () => void;
  onSave: (address: AddressDto) => Promise<void>;
}

const AddressManageModal: React.FC<AddressManageModalProps> = ({ address, onClose, onSave }) => {
  const [formData, setFormData] = useState<AddressDto>({
    nickname: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData(address);
    }
  }, [address]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressAutocomplete = (addr: AddressDto) => {
    setFormData(prev => ({
      ...prev,
      street: addr.street || prev.street,
      number: addr.number || prev.number,
      neighborhood: addr.neighborhood || prev.neighborhood,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      zipCode: addr.zipCode || prev.zipCode,
      latitude: addr.latitude,
      longitude: addr.longitude,
    }));
  };

  const handleZipCodeBlur = async () => {
    const zipCode = formData.zipCode.replace(/\D/g, '');
    if (zipCode.length === 8) {
      try {
        setIsLoading(true);
        const data = await zipCodeApi.getAddressByZipCode(zipCode);
        setFormData(prev => ({
          ...prev,
          street: data.logradouro || prev.street,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.localidade || prev.city,
          state: data.uf || prev.state,
        }));
      } catch (error) {
        toast.error('CEP não encontrado.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.street || !formData.number || !formData.neighborhood || !formData.city || !formData.state || !formData.zipCode) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar endereço.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
        <GlobalModalHeader>
          <div style={{ width: '32px' }} />
          <GlobalModalTitle>{address?.id ? 'Editar Endereço' : 'Novo Endereço'}</GlobalModalTitle>
          <GlobalCloseButton onClick={onClose}>×</GlobalCloseButton>
        </GlobalModalHeader>

        <ModalBody>
          <FormContainer as="form" onSubmit={handleSubmit}>
            <FormInput
              label="Apelido (ex: Casa, Trabalho)"
              name="nickname"
              value={formData.nickname}
              onChange={handleChange}
              placeholder="Dê um nome para este endereço"
            />

            <AddressAutocomplete
              label="Buscar endereço"
              placeholder="Digite rua, número e cidade"
              onSelect={handleAddressAutocomplete}
            />

            <FormInput
              label="CEP*"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              onBlur={handleZipCodeBlur}
              placeholder="00000-000"
              required
            />

            <FormRow grid="2fr 1fr">
              <FormInput
                label="Rua*"
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder="Rua das Flores"
                required
              />
              <FormInput
                label="Número*"
                name="number"
                value={formData.number}
                onChange={handleChange}
                placeholder="123"
                required
              />
            </FormRow>

            <FormRow>
              <FormInput
                label="Complemento"
                name="complement"
                value={formData.complement}
                onChange={handleChange}
                placeholder="Apt 101, Bloco A"
              />
              <FormInput
                label="Bairro*"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                placeholder="Centro"
                required
              />
            </FormRow>

            <FormRow>
              <FormInput
                label="Cidade*"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="São Paulo"
                required
              />
              <FormInput
                label="Estado*"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="SP"
                required
              />
            </FormRow>

            <ButtonGroup>
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? 'Salvando...' : 'Salvar Endereço'}
              </Button>
            </ButtonGroup>
          </FormContainer>
        </ModalBody>
    </Modal>
  );
};

export default AddressManageModal;
